import { z } from 'zod';
import { conditionSchema } from './condition.js';
import { pathSchema, traitKeySchema } from './content-id.js';
/**
 * Эффект — данные, а не код (D5). Четыре поля: что меняем, какой
 * операцией, на что и при каком условии (rules-engine.md, раздел 1).
 *
 * Операций пять: `set`, `add`, `add_capped`, `mul`, `clamp` — над
 * числовыми величинами, и `grant` — над наборами признаков (D18.1,
 * D21). Кости в модификаторах и лечение с уроном операциями движка
 * не являются (D21.4, D21.5).
 *
 * Формат эффекта — публичный контракт (D5): он расширяется решением
 * в decisions.md, а не правкой этой схемы «по ходу дела».
 */
/** Ссылка на другую величину: разрешается до применения операции. */
export const effectRefSchema = z.strictObject({ ref: pathSchema });
/** Значение для `set` и `add`: число либо ссылка. */
export const scalarValueSchema = z.union([z.number(), effectRefSchema]);
/**
 * Границы для `clamp`. Каждая необязательна, но `clamp` без единой
 * границы ничего не ограничивает — такой эффект не имеет смысла
 * и означает ошибку в данных, поэтому отклоняется схемой.
 */
export const clampValueSchema = z
    .strictObject({
    min: z.number().optional(),
    max: z.number().optional(),
})
    .refine((value) => value.min !== undefined || value.max !== undefined, 'у clamp должна быть хотя бы одна граница: min или max')
    .refine((value) => value.min === undefined || value.max === undefined || value.min <= value.max, 'у clamp min не может быть больше max');
/**
 * Значение для `add_capped` (D21.1): что прибавляем и каким пределом
 * режем вклад. Предел — на слагаемое, а не на итог: этим операция
 * и отличается от `clamp`.
 */
export const cappedValueSchema = z.strictObject({
    amount: scalarValueSchema,
    max: z.number(),
});
/**
 * Множитель для `mul` (D21.2). Ноль допустим — «скорость 0» бывает
 * состоянием; отрицательный множитель бессмыслен и отклоняется.
 */
export const multiplierSchema = z.number().nonnegative();
const conditionField = conditionSchema.nullable();
const setEffectSchema = z.strictObject({
    target: pathSchema,
    op: z.literal('set'),
    value: scalarValueSchema,
    condition: conditionField,
});
const addEffectSchema = z.strictObject({
    target: pathSchema,
    op: z.literal('add'),
    value: scalarValueSchema,
    condition: conditionField,
});
const clampEffectSchema = z.strictObject({
    target: pathSchema,
    op: z.literal('clamp'),
    value: clampValueSchema,
    condition: conditionField,
});
const cappedEffectSchema = z.strictObject({
    target: pathSchema,
    op: z.literal('add_capped'),
    value: cappedValueSchema,
    condition: conditionField,
});
const mulEffectSchema = z.strictObject({
    target: pathSchema,
    op: z.literal('mul'),
    value: multiplierSchema,
    condition: conditionField,
});
/** Добавление признака в набор (D21.3). */
const grantEffectSchema = z.strictObject({
    target: pathSchema,
    op: z.literal('grant'),
    value: traitKeySchema,
    condition: conditionField,
});
/**
 * Форма значения зависит от операции, поэтому эффект — размеченное
 * объединение по `op`: `clamp` с числом, `add` с границами и `mul`
 * с признаком проверку не проходят.
 */
export const effectSchema = z.discriminatedUnion('op', [
    setEffectSchema,
    addEffectSchema,
    cappedEffectSchema,
    mulEffectSchema,
    clampEffectSchema,
    grantEffectSchema,
]);
/** Операции над числом и операция над набором признаков — разные миры. */
export const numericOps = ['set', 'add', 'add_capped', 'mul', 'clamp'];
export const traitOps = ['grant'];
//# sourceMappingURL=effect.js.map